/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Fetches and caches organization country via /api/organization (with retry and dedupe).
 */

import { captureMessage } from "@/lib/monitoring/sentry";
import { logger } from "@/lib/utils/logger";
import { useEffect, useRef, useState } from "react";

const COUNTRY_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 2;
const INITIAL_DELAY_MS = 2000;
const REQUEST_TIMEOUT_MS = 10000;

type SessionLike = { getToken: () => Promise<string | null> };

const countryCache = new Map<
  string,
  { country: string | null; timestamp: number }
>();

function getCachedCountry(organizationId: string): string | null {
  const entry = countryCache.get(organizationId);
  if (!entry) return null;
  if (Date.now() - entry.timestamp >= COUNTRY_CACHE_TTL_MS) {
    countryCache.delete(organizationId);
    return null;
  }
  return entry.country;
}

function setCachedCountry(
  organizationId: string,
  country: string | null,
): void {
  countryCache.set(organizationId, {
    country,
    timestamp: Date.now(),
  });
}

export function clearCountryCache(): void {
  countryCache.clear();
}

async function fetchOrgCountry(
  organizationId: string,
  retryCount: number,
): Promise<string | null> {
  try {
    const queryParams = new URLSearchParams({
      page: "1",
      limit: "1",
      includeSettings: "false",
      includeMembers: "false",
    });

    const res = await fetch(`/api/organization?${queryParams.toString()}`, {
      headers: {
        "X-Organization-Id": organizationId,
      },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    if (!res.ok) {
      if (res.status >= 500 && retryCount < MAX_RETRIES) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, retryCount);
        logger.debug("[ORG COUNTRY] Retrying organization fetch", {
          retryCount: retryCount + 1,
          delay,
          status: res.status,
        });
        await new Promise((r) => setTimeout(r, delay));
        return fetchOrgCountry(organizationId, retryCount + 1);
      }
      return null;
    }

    const payload = await res.json();
    const orgData = payload?.data ?? payload;
    const country = orgData?.country ?? null;
    setCachedCountry(organizationId, country);
    return country;
  } catch (error) {
    if (isConnectionError(error) && retryCount < MAX_RETRIES) {
      const delay = INITIAL_DELAY_MS * Math.pow(2, retryCount);
      logger.debug("[ORG COUNTRY] Retrying after connection error", {
        retryCount: retryCount + 1,
        delay,
      });
      await new Promise((r) => setTimeout(r, delay));
      return fetchOrgCountry(organizationId, retryCount + 1);
    }
    logger.warn(
      "[ORG COUNTRY] Failed to load organization country after retries",
      {
        error: error instanceof Error ? error.message : "Unknown error",
      },
    );
    return null;
  }
}

function isConnectionError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  const msg =
    error instanceof Error
      ? error.message.toLowerCase()
      : String(error).toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("network") ||
    msg.includes("econnrefused") ||
    msg.includes("econnreset") ||
    msg.includes("aborted") ||
    msg.includes("timeout")
  );
}

export function useOrganizationCountry(
  organizationId: string | null,
  session: SessionLike | null,
  isTestMode: boolean,
): string | null {
  const [country, setCountry] = useState<string | null>(() => {
    if (isTestMode) return "US";
    if (!organizationId) return null;
    return getCachedCountry(organizationId);
  });

  const fetchRef = useRef<Promise<string | null> | null>(null);

  useEffect(() => {
    if (isTestMode) {
      setCountry("US");
      return;
    }
    if (!organizationId || !session) {
      setCountry(null);
      return;
    }

    const cached = getCachedCountry(organizationId);
    if (cached !== null) {
      setCountry(cached);
      return;
    }

    if (fetchRef.current) {
      fetchRef.current
        .then(setCountry)
        .catch((err: unknown) => {
          logger.warn("[ORG COUNTRY] Deduped fetch rejected (non-blocking)", {
            operation: "ORG_COUNTRY_DEDUPED_FETCH",
            organizationId,
            error: err instanceof Error ? err.message : String(err),
          });
          void captureMessage(
            "[ORG COUNTRY] Deduped fetch rejected (non-blocking)",
            {
              level: "warning",
              tags: {
                swallow: "intentional_non_blocking",
                operation: "ORG_COUNTRY_DEDUPED_FETCH",
                surface: "organization_country",
              },
            },
          );
          setCountry(null);
        });
      return;
    }

    const load = (): Promise<string | null> =>
      fetchOrgCountry(organizationId, 0);

    const p = load();
    fetchRef.current = p;
    p.then((c) => {
      setCountry(c);
      fetchRef.current = null;
    }).catch((err: unknown) => {
      logger.warn("[ORG COUNTRY] Primary fetch rejected (non-blocking)", {
        operation: "ORG_COUNTRY_PRIMARY_FETCH",
        organizationId,
        error: err instanceof Error ? err.message : String(err),
      });
      void captureMessage(
        "[ORG COUNTRY] Primary fetch rejected (non-blocking)",
        {
          level: "warning",
          tags: {
            swallow: "intentional_non_blocking",
            operation: "ORG_COUNTRY_PRIMARY_FETCH",
            surface: "organization_country",
          },
        },
      );
      setCountry(null);
      fetchRef.current = null;
    });
  }, [organizationId, session, isTestMode]);

  return country;
}
